import React, { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wrench, RefreshCw, Hammer, Trash2, HelpCircle, Camera, Upload, X,
  ArrowRight, ArrowLeft, CalendarDays, CheckCircle2, Check, Ban,
} from "lucide-react";
import config from "../../config";
import { useCityPrice } from "../../hooks/useCityPrice";
import { DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD     = 3;

const WORK_TYPE_ICONS = { repair: Wrench, replace: RefreshCw, install: Hammer, remove: Trash2, notsure: HelpCircle };

const INCLUDED_ITEMS = [
  "Switchboard",
  "Switchboard Box",
  "Selected switchboard/plate",
  "Basic electrical connection",
  "Up to 2.5m wiring",
  "Installation labour",
  "Basic testing",
];
const NOT_INCLUDED_ITEMS = [
  "More than 2.5m wiring",
  "Complete house wiring",
  "Major rewiring",
  "Major wall breaking",
  "Plastering / painting",
  "Main electrical panel work",
  "Appliance repair",
];

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

// Custom "Other" point counts aren't in the catalog — computed live from the
// same base+per-point formula the 2/4/6/8/12/16 catalog tiers follow.
const customPointsBasePrice = (n) => Math.max(0, 349 + Math.max(0, n - 2) * 30);

const STEPS = [
  { key: "service",  label: "Service",     sub: "Repair, replace, install, remove or not sure" },
  { key: "points",   label: "Points",      sub: "Number of points"                              },
  { key: "schedule", label: "Date & Time", sub: "Photo & book your visit"                       },
];

const SwitchboardBookingPage = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const service = useMemo(
    () => config.SERVICE_CONFIG?.technical?.services?.["electrical-services"]?.types?.find((t) => t.value === "switchboard"),
    []
  );
  const pointOptions = service?.subCategories?.[0]?.types || [];

  const [step, setStep] = useState("service");
  const [workTypeKey, setWorkTypeKey] = useState(null);
  const [switchboardType, setSwitchboardType] = useState(null); // "new" | "old" | null
  const [points, setPoints] = useState(null);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customPointsValue, setCustomPointsValue] = useState("");
  const [customPoints, setCustomPoints] = useState(null); // confirmed number
  const [photoDataUrl, setPhotoDataUrl] = useState("");

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const workType = service?.workTypes?.find((wt) => wt.key === workTypeKey) || null;
  const isNotSureFlow = workTypeKey === "notsure";
  const isPointsNotSure = points?.notSure;

  const cityBasePrice = (value, basePrice) => cityPrice(value, basePrice);
  const labour = (() => {
    if (isNotSureFlow || isPointsNotSure) return 0;
    if (customPoints) return Math.round(cityBasePrice(`switchboard_custom_${customPoints}`, customPointsBasePrice(customPoints)) * (workType?.multiplier || 1));
    if (points) return Math.round(cityBasePrice(points.value, points.price) * (workType?.multiplier || 1));
    return 0;
  })();
  const hasStartingPrice = isNotSureFlow || isPointsNotSure || workTypeKey === "install";
  // Not Sure flows have no priced item yet — the visit/inspection fee
  // stands in as the subtotal until the electrician assesses on-site.
  const subtotal = (isNotSureFlow || isPointsNotSure) ? cityBasePrice("switchboard_inspection", 149) : labour;
  const tax = Number(((subtotal * 18) / 100).toFixed(2));
  const surcharge = Number(((subtotal * 5) / 100).toFixed(2));
  const grandTotal = Number((subtotal + tax + surcharge).toFixed(2));

  const chooseWorkType = (key) => {
    setWorkTypeKey(key);
    setPoints(null); setCustomPoints(null); setShowCustomInput(false); setCustomPointsValue("");
    if (key === "notsure") {
      setSwitchboardType(null);
      goToSchedule();
    } else {
      setSwitchboardType(key === "install" ? "new" : "old");
      setStep("points");
    }
  };

  const choosePoints = (leaf) => {
    if (leaf.value === "switchboard_other") {
      setShowCustomInput(true);
      return;
    }
    setPoints(leaf);
    setCustomPoints(null);
    goToSchedule();
  };

  const confirmCustomPoints = () => {
    const n = parseInt(customPointsValue, 10);
    if (!n || n <= 0) return;
    setPoints({ value: "switchboard_other", label: "Other" });
    setCustomPoints(n);
    goToSchedule();
  };

  const handlePhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoDataUrl(String(reader.result || ""));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const goBack = () => {
    if (step === "schedule") {
      if (isNotSureFlow) { setWorkTypeKey(null); setStep("service"); }
      else { setStep("points"); setShowCustomInput(false); }
      return;
    }
    if (step === "points") { setWorkTypeKey(null); setSwitchboardType(null); setStep("service"); return; }
    navigate("/schedule-booking/technical");
  };

  const goToSchedule = async () => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const pointsLabel = () => {
    if (isNotSureFlow) return "Not Sure — Inspection Required";
    if (isPointsNotSure) return "Not Sure — Inspection Required";
    if (customPoints) return `${customPoints} Points`;
    if (points) return `${points.label} Points`;
    return "";
  };

  const handleCheckout = async () => {
    if (!selectedDate || !selectedTime || !workType) return;
    setCheckoutLoading(true);

    try {
      const typeLabel = switchboardType === "new" ? "New" : switchboardType === "old" ? "Old" : "";
      const title = [`${workType.label} Switchboard`, typeLabel, pointsLabel()].filter(Boolean).join(" — ");
      const cartItem = {
        id: `switchboard-${workTypeKey}-${points?.value || "notsure"}-${customPoints || ""}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
        title,
        name: title,
        short: [
          photoDataUrl ? "Photo attached" : null,
          hasStartingPrice ? "Starting price — technician confirms final amount on-site" : null,
        ].filter(Boolean).join(" · ") || "No additional notes",
        subtitle: "1 unit",
        category: "Switchboard",
        price: subtotal,
        image: points?.image || service?.image || DEFAULT_HERO_IMAGE,
        qty: 1,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: "technical",
      };

      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => i.id !== cartItem.id) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, cartItem]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      // Back to the shared technical-services cart-building page (already
      // written to the shared cart above) instead of straight to checkout,
      // so other services can still be added before paying once for all.
      if (isUserLoggedIn()) {
        navigate("/schedule-booking/technical");
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/schedule-booking/technical" } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!service) {
    return <div style={S.page}><div style={S.container}><div style={S.errorBox}>Switchboard service is not configured.</div></div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/schedule-booking/technical" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>Switchboard</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><CalendarDays size={13} /> SWITCHBOARD BOOKING</div>
          <h1 style={S.heroTitle}>Book Switchboard Repair, Replace, Install, Remove or Not Sure</h1>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── What do you need? (work type) ───────────────────────────────── */}
        {step === "service" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What do you need?</h2>
            <p style={S.sectionSub}>Choose the service for your switchboard</p>
            <div style={S.workTypeGrid}>
              {service.workTypes.map((wt) => {
                const Icon = WORK_TYPE_ICONS[wt.key] || Wrench;
                return (
                  <button key={wt.key} type="button" style={S.workTypeCard} onClick={() => chooseWorkType(wt.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={22} color="#16a34a" /></div>
                    <div style={S.workTypeLabel}>{wt.label}</div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Number of Points ──────────────────────────────────────────────── */}
        {step === "points" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>How Many Points?</h2>
            <p style={S.sectionSub}>Just give an approximate number — the electrician verifies the actual size</p>
            <div style={S.pointsGrid}>
              {pointOptions.map((leaf) => (
                <button key={leaf.value} type="button" style={S.pointsCard} onClick={() => choosePoints(leaf)}>
                  {leaf.label}
                </button>
              ))}
            </div>
            {showCustomInput && (
              <div style={S.customPointsRow}>
                <input
                  type="number"
                  min="1"
                  style={S.customPointsInput}
                  placeholder="Enter number of points"
                  value={customPointsValue}
                  onChange={(e) => setCustomPointsValue(e.target.value)}
                />
                <button
                  type="button"
                  style={{ ...S.continueBtn, ...S.noteFooterBtn, ...(!customPointsValue ? S.continueBtnDisabled : {}) }}
                  disabled={!customPointsValue}
                  onClick={confirmCustomPoints}
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Photo + Date & Time (combined screen) ─────────────────────────── */}
        {step === "schedule" && (
          <>
            <div style={S.summaryBar}>
              <div style={S.summaryBody}>
                <div style={S.summaryTitle}>Switchboard Booking Summary</div>
                <div style={S.summaryChipRow}>
                  <span style={S.summaryChip}>
                    {workType?.label} Switchboard{switchboardType ? ` — ${switchboardType === "new" ? "New" : "Old"}` : ""} — {pointsLabel()}
                  </span>
                  <span style={S.summaryDurationChip}>{hasStartingPrice ? "Starting " : ""}₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>
              <button type="button" style={S.summaryChangeBtn} onClick={goBack}>
                Change
              </button>
            </div>

            <div style={S.card}>
              <h2 style={S.sectionTitle}>📷 Switchboard Photo</h2>
              <p style={S.sectionSub}>
                {isNotSureFlow || isPointsNotSure
                  ? "Strongly recommended — helps the electrician verify the actual switchboard size"
                  : "Optional — helps the electrician come prepared"}
              </p>
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={S.hiddenInput} onChange={handlePhotoFile} />
              <input ref={photoInputRef} type="file" accept="image/*" style={S.hiddenInput} onChange={handlePhotoFile} />
              {photoDataUrl ? (
                <div style={S.photoPreviewRow}>
                  <img src={photoDataUrl} alt="Attached" style={S.photoPreview} />
                  <button type="button" style={S.addedRemoveBtn} onClick={() => setPhotoDataUrl("")}>
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div style={S.photoBtnRow}>
                  <button type="button" style={S.photoBtn} onClick={() => cameraInputRef.current?.click()}>
                    <Camera size={16} /> Take Photo
                  </button>
                  <button type="button" style={S.photoBtn} onClick={() => photoInputRef.current?.click()}>
                    <Upload size={16} /> Upload Photo
                  </button>
                </div>
              )}
            </div>

            <div style={S.scheduleHead}>
              <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
              <div style={S.legend}>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
              </div>
            </div>

            <div style={S.scheduleColumns}>
              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>1. Select Date</div>
                {loadingAvailability ? (
                  <div style={S.stateBox}>Loading…</div>
                ) : availabilityError ? (
                  <div style={S.errorBox}>{availabilityError}</div>
                ) : (
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button"
                          disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                <div style={S.slotGrid}>
                  {(activeDay?.slots || []).map((slot) => {
                    const state      = getSlotState(slot);
                    const selectable = state === "available";
                    const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                    const selected   = selectedTime === slot.time;
                    return (
                      <button
                        key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        style={{
                          ...S.slotBtn,
                          ...(selected ? S.slotBtnActive : {}),
                          ...(state === "booked" ? S.slotBtnBooked : {}),
                          ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                        }}
                        onClick={() => selectable && pickTime(slot.time, slot._id)}
                      >
                        <div style={S.slotTimeRow}>
                          <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                          {selected && <CheckCircle2 size={13} color="#fff" />}
                        </div>
                        <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                          {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>{(isNotSureFlow || isPointsNotSure) ? "Visit / Inspection" : "Switchboard / Service"}</span><span>₹{subtotal.toFixed(2)}</span></div>
              <div style={S.breakdownRow}><span>Box</span><span>Included</span></div>
              <div style={S.breakdownRow}><span>Basic Wiring (up to 2.5m)</span><span>Included</span></div>
              <div style={S.breakdownRow}><span>Tax &amp; Surcharge</span><span>₹{(tax + surcharge).toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>{hasStartingPrice ? "Starting Price" : "TOTAL"}</span><span>₹{grandTotal.toFixed(2)}</span></div>
            </div>

            <div style={S.includedRow}>
              <div style={S.includedBox}>
                <div style={S.includedTitle}>Included</div>
                {INCLUDED_ITEMS.map((item) => (
                  <div key={item} style={S.includedItem}><Check size={13} color="#16a34a" /> {item}</div>
                ))}
              </div>
              <div style={S.notIncludedBox}>
                <div style={S.notIncludedTitle}>Not Included</div>
                {NOT_INCLUDED_ITEMS.map((item) => (
                  <div key={item} style={S.notIncludedItem}><Ban size={13} color="#b91c1c" /> {item}</div>
                ))}
              </div>
            </div>
            <div style={S.noteBox}>
              Up to 2.5m basic wiring included. Additional wiring, if required, will be charged separately after your approval.
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!selectedTime || checkoutLoading ? S.continueBtnDisabled : {}) }}
              disabled={!selectedTime || checkoutLoading}
              onClick={handleCheckout}
            >
              {checkoutLoading ? "Booking..." : isUserLoggedIn() ? "Book Now" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "660px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },

  workTypeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" },
  workTypeCard: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  workTypeIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  workTypeLabel: { fontSize: "15px", fontWeight: 800, color: "#111827", flex: 1 },

  pointsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: "12px" },
  pointsCard: { padding: "18px 8px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", fontSize: "16px", fontWeight: 800, color: "#111827", textAlign: "center" },

  customPointsRow: { display: "flex", gap: "12px", marginTop: "16px", flexWrap: "wrap", alignItems: "center" },
  customPointsInput: { flex: "1 1 200px", padding: "12px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "13px", fontFamily: "inherit", boxSizing: "border-box" },

  hiddenInput: { display: "none" },
  photoBtnRow: { display: "flex", gap: "12px", flexWrap: "wrap" },
  photoBtn: { display: "inline-flex", alignItems: "center", gap: "8px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "11px 18px", borderRadius: "10px" },
  photoPreviewRow: { display: "flex", alignItems: "center", gap: "12px" },
  photoPreview: { width: "90px", height: "90px", objectFit: "cover", borderRadius: "12px", border: "1px solid #e5e7eb" },

  addedRemoveBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
  summaryBody: { minWidth: 0, flex: 1 },
  summaryTitle: { fontSize: "15px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },

  includedRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "14px", marginBottom: "14px" },
  includedBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "14px 16px" },
  includedTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  includedItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: "#166534", padding: "3px 0" },
  notIncludedBox: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "14px", padding: "14px 16px" },
  notIncludedTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  notIncludedItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: "#991b1b", padding: "3px 0" },

  noteBox: { fontSize: "12px", color: "#6b7280", background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "10px", padding: "10px 12px", marginBottom: "16px", lineHeight: 1.4 },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  noteFooterBtn: { width: "auto", padding: "13px 24px", marginTop: 0 },
};

export default SwitchboardBookingPage;
